import React, { useState, useEffect } from 'react';
import {
  Field,
  RichText,
  SitecoreContext,
  withSitecoreContext,
} from '@sitecore-jss/sitecore-jss-react';
import { useLocation } from 'react-router-dom';

import './index.scss';

interface BannerProps {
  displayName: string;
  id: string;
  name: string;
  url: string;
  fields: {
    'Banner Title': Field<string>;
    'Banner Enabled': Field<boolean>;
    'Banner Content': Field<string>;
    'Color Schema': Field<string>;
    'Display On Subpages': Field<boolean>;
    'Display On Page': {
      url: string;
      name: string;
    };
  };
}

type FooterBannerProps = {
  fields: {
    items: BannerProps[];
  };
  sitecoreContext: SitecoreContext & { pageEditing: boolean };
};

const FooterBanner = ({ fields, sitecoreContext }: FooterBannerProps): JSX.Element => {
  if (sitecoreContext?.pageEditing) {
    return <></>;
  }

  const location = useLocation();

  const [activeBanner, setActiveBanner] = useState<BannerProps | null>(null);

  /**
   * ------------------------------------------------------------
   * HELPERS
   * ------------------------------------------------------------
   */
  const stripTrailingSlash = (str: string) => (str.endsWith('/') ? str.slice(0, -1) : str);

  const endsWithAny = (str: string, suffixes: string[]) =>
    suffixes.some((suffix) => str.endsWith(suffix));

  const checkLanguageURL = (url: string) => {
    return endsWithAny(url, ['/en', '/hr']);
  };

  const getCookie = (item: BannerProps) => {
    const nameEQ = 'upmcBanner' + item.id + '=';
    const cookies = document.cookie.split(';');

    for (let c of cookies) {
      c = c.trim();
      if (c.indexOf(nameEQ) === 0) {
        return false;
      }
    }
    return true;
  };

  const urlMatches = (item: BannerProps) => {
    const pathname = stripTrailingSlash(window.location.pathname.toLowerCase());
    const href = window.location.href;

    if (item.fields['Display On Page'] === null) {
      return false;
    }

    const pageUrl = stripTrailingSlash(item.fields['Display On Page']?.url?.toLowerCase() || '');

    if (pathname === pageUrl) return true;

    if (item.fields['Display On Subpages']?.value === true && pathname.startsWith(pageUrl)) {
      return true;
    }

    if (checkLanguageURL(href) && pageUrl === '') {
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (!fields?.items?.length) {
      setActiveBanner(null);
      return;
    }

    const banner = fields.items.find((item) => {
      return item.fields['Banner Enabled']?.value === true && urlMatches(item) && getCookie(item);
    });

    setActiveBanner(banner || null);
  }, [fields, location.pathname]);

  /**
   * ------------------------------------------------------------
   * RENDER
   * ------------------------------------------------------------
   */
  if (!activeBanner) {
    return <></>;
  }

  const colorSchema = activeBanner.fields['Color Schema']?.value || 'gray94';

  return (
    <div className={`footer-banner-wrapper ${colorSchema}`}>
      <div className={`footer-banner ${colorSchema}`}>
        <RichText className="footer-banner-content" field={activeBanner.fields['Banner Content']} />
      </div>
    </div>
  );
};

export default withSitecoreContext()(FooterBanner);
